import { faCircle } from '@fortawesome/pro-light-svg-icons';
import { faBox, faBoxesStacked } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BusinessProductInventory, Product } from '@spike/product-model';
import clsx from 'clsx';
import NumberField from 'components/UI/V3/Forms/NumberField';
import { Switch } from 'components/UI/V3/Forms/Switch';
import Button from 'components/UI/V3/Button/Button';
import clone from 'lodash/clone';
import { FunctionComponent } from 'react';
import styles from './Inventory.module.scss';

interface Props {
    product: Product;
    saving?: boolean;
    onChange?: (product: Product) => void;
    onSave: () => void;
}

const defaultInventory: BusinessProductInventory = {
    inventoryTracking: true,
    lowStockNotifications: false,
    lowStockThreshold: 0,
    trackBatches: true
};

export const buildBusinessProductInventory = (
    inventory?: Partial<BusinessProductInventory> | null
): BusinessProductInventory => {
    const lowStockNotifications = Boolean(inventory?.lowStockNotifications);
    const threshold = Number(inventory?.lowStockThreshold);
    const inventoryPayload: BusinessProductInventory = {
        inventoryTracking: inventory?.inventoryTracking !== false,
        lowStockNotifications,
        lowStockThreshold: lowStockNotifications && !Number.isNaN(threshold) ? threshold : 0,
        trackBatches: inventory?.trackBatches === true
    };

    if (inventory?.status) {
        inventoryPayload.status = inventory.status;
    }

    return inventoryPayload;
};

export const Inventory: FunctionComponent<Props> = props => {
    const inventory: BusinessProductInventory = {
        ...defaultInventory,
        ...props.product.businessProduct?.inventory
    };
    const isTrackingEnabled = inventory.inventoryTracking !== false;
    const isLotsSelected = inventory.trackBatches === true;

    const updateInventory = (patch: Partial<BusinessProductInventory>) => {
        const updatedProduct = clone(props.product);
        updatedProduct.businessProduct = {
            ...updatedProduct.businessProduct!,
            inventory: buildBusinessProductInventory({
                ...defaultInventory,
                ...updatedProduct.businessProduct?.inventory,
                ...patch
            })
        };
        props.onChange?.(updatedProduct);
    };

    const selectTotalStock = () => {
        updateInventory({
            trackBatches: false
        });
    };

    const selectLots = () => {
        updateInventory({
            trackBatches: true
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.trackingSwitch}>
                <p className={styles.trackingSwitchTitle}>Track Stock Quantity</p>
                <Switch
                    checked={isTrackingEnabled}
                    onChange={checked => updateInventory({ inventoryTracking: checked })}
                />
            </div>

            {isTrackingEnabled && (
                <>
                    <div className={styles.options}>
                        <button
                            type="button"
                            className={clsx(styles.card, !isLotsSelected && styles.cardSelected)}
                            aria-pressed={!isLotsSelected}
                            onClick={selectTotalStock}
                        >
                            <div className={styles.cardContent}>
                                <FontAwesomeIcon
                                    icon={faBox}
                                    className={styles.cardIcon}
                                />
                                <div className={styles.cardText}>
                                    <p className={styles.cardTitle}>Track Total Stock</p>
                                    <p className={styles.cardDescription}>
                                        Track inventory with simple stock adjustments.
                                    </p>
                                </div>
                            </div>
                            <FontAwesomeIcon
                                icon={!isLotsSelected ? faCircleCheck : faCircle}
                                className={clsx(
                                    styles.cardCheck,
                                    !isLotsSelected ? styles.selectedIcon : styles.unselectedIcon
                                )}
                            />
                        </button>

                        <span className={styles.or}>or</span>

                        <button
                            type="button"
                            className={clsx(styles.card, isLotsSelected && styles.cardSelected)}
                            aria-pressed={isLotsSelected}
                            onClick={selectLots}
                        >
                            <div className={styles.cardContent}>
                                <FontAwesomeIcon
                                    icon={faBoxesStacked}
                                    className={styles.cardIcon}
                                />
                                <div className={styles.cardText}>
                                    <p className={styles.cardTitle}>Track by Lots (Advanced)</p>
                                    <p className={styles.cardDescription}>
                                        Track inventory by delivery or lot for greater control.
                                    </p>
                                </div>
                            </div>
                            <FontAwesomeIcon
                                icon={isLotsSelected ? faCircleCheck : faCircle}
                                className={clsx(
                                    styles.cardCheck,
                                    isLotsSelected ? styles.selectedIcon : styles.unselectedIcon
                                )}
                            />
                        </button>
                    </div>

                    <div className={styles.notifications}>
                        <div>
                            <div className={styles.notificationsHeader}>
                                <p className={styles.notificationsTitle}>Receive Low Stock Notifications</p>
                                <Switch
                                    checked={inventory.lowStockNotifications}
                                    onChange={checked =>
                                        updateInventory({
                                            lowStockNotifications: checked,
                                            lowStockThreshold: checked ? inventory.lowStockThreshold : 0
                                        })
                                    }
                                />
                            </div>
                            <p className={styles.notificationsDescription}>
                                Cuddles will notify you when inventory reaches a low stock level.
                            </p>
                        </div>

                        {inventory.lowStockNotifications && (
                            <div className={styles.lowStockField}>
                                <NumberField
                                    label="Low Stock Level"
                                    name="lowStockThreshold"
                                    value={inventory.lowStockThreshold}
                                    placeholder="0"
                                    required
                                    onChange={value => updateInventory({ lowStockThreshold: value ?? 0 })}
                                    scale={0}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}

            <div className={styles.actions}>
                <Button
                    label="Save"
                    size="large"
                    variant="primary"
                    isLoading={props.saving}
                    disabled={props.saving}
                    onClick={props.onSave}
                />
            </div>
        </div>
    );
};

export default Inventory;
